import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Order } from '../../types/admin';
import { doc, updateDoc, collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import styles from '../../styles/admin/Dashboard.module.css';
import { FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

// Create icon components with proper typing
const SortIcon: IconType = FaSort;
const SortUpIcon: IconType = FaSortUp;
const SortDownIcon: IconType = FaSortDown;
const SearchIcon: IconType = FaSearch;

const ORDER_STATUS_OPTIONS = [
  { value: 1, label: 'Pending' },
  { value: 2, label: 'Completed' },
  { value: 3, label: 'Cancelled' }
];

const ITEMS_PER_PAGE_OPTIONS = [
  { value: 5, label: '5 per page' },
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
];

type SortField = 'orderDate' | 'total' | 'customer';
type SortDirection = 'asc' | 'desc';

const Orders: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('orderDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(
        ordersRef,
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(ordersQuery);
      const newOrders = snapshot.docs.map(doc => ({
        ...doc.data(),
        orderId: doc.id
      })) as Order[];

      console.log(newOrders);
      setOrders(newOrders);
    } catch (error) {
      setError('Error fetching orders');
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'MMM dd, yyyy HH:mm');
    } catch {
      return date;
    }
  };

  const handleOrderStatusChange = async (orderId: string, newStatus: number) => {
    if (!orderId) {
      setUpdateError('Invalid order ID');
      return;
    }

    setIsSubmitting(true);
    setUpdateError(null);

    try {
      const orderRef = doc(collection(db, 'orders'), orderId);
      await updateDoc(orderRef, {
        orderStatus: newStatus,
        updatedAt: new Date().toISOString()
      });

      // Update the local state to reflect the change immediately
      const updatedOrders = orders.map(order => {
        if (order.orderId === orderId) {
          return {
            ...order,
            orderStatus: newStatus,
            updatedAt: new Date().toISOString()
          };
        }
        return order;
      });

      // Force a re-render by updating the orders array
      orders.splice(0, orders.length, ...updatedOrders);
    } catch (error) {
      console.error('Error updating order status:', error);
      setUpdateError('Failed to update order status. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleModalOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the overlay itself, not its children
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return '';
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const filterAndSortOrders = (orders: Order[]) => {
    return orders
      .filter(order => {
        // Status filter
        if (statusFilter !== 'all' && order.orderStatus !== statusFilter) {
          return false;
        }

        // Search term filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const customerName = `${order.billingInfo?.firstName || ''} ${order.billingInfo?.lastName || ''}`.toLowerCase();
          const items = order.items?.map(item => item.name).join(' ').toLowerCase() || '';
          const total = order.financialInfo?.total?.toString() || '';
          const date = formatDate(order.orderDate)?.toLowerCase() || '';

          return (
            customerName.includes(searchLower) ||
            items.includes(searchLower) ||
            total.includes(searchLower) ||
            date.includes(searchLower)
          );
        }

        return true;
      })
      .sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'orderDate':
            comparison = new Date(a.orderDate || '').getTime() - new Date(b.orderDate || '').getTime();
            break;
          case 'total':
            comparison = (a.financialInfo?.total || 0) - (b.financialInfo?.total || 0);
            break;
          case 'customer':
            comparison = `${a.billingInfo?.firstName || ''} ${a.billingInfo?.lastName || ''}`.localeCompare(
              `${b.billingInfo?.firstName || ''} ${b.billingInfo?.lastName || ''}`
            );
            break;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  };

  const filteredAndSortedOrders = filterAndSortOrders(orders);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = filteredAndSortedOrders.slice(startIndex, endIndex);

  const renderPagination = () => {
    const totalPages = Math.ceil(filteredAndSortedOrders.length / itemsPerPage);
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={styles.paginationButton}
      >
        Previous
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={styles.paginationButton}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="start-ellipsis" className={styles.paginationEllipsis}>...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`${styles.paginationButton} ${currentPage === i ? styles.active : ''}`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="end-ellipsis" className={styles.paginationEllipsis}>...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={styles.paginationButton}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={styles.paginationButton}
      >
        Next
      </button>
    );

    return pages;
  };

  if (isLoading) {
    return (
      <div className={styles.adminDashboardLoading}>
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <div className={styles.ordersContainer}>
      {updateError && (
        <div className={styles.errorMessage}>
          {updateError}
        </div>
      )}

      <div className={styles.ordersFilters}>
        <div className={styles.searchBox}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by date, customer, items, or total..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterControls}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className={styles.statusFilter}
          >
            <option value="all">All Statuses</option>
            {ORDER_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className={styles.perPageSelect}
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.ordersInfo}>
        Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedOrders.length)} of {filteredAndSortedOrders.length} orders
      </div>

      <table className={styles.ordersTable}>
        <thead>
          <tr>
            <th onClick={() => handleSort('orderDate')} className={styles.sortableHeader}>
              ORDER DATE{getSortIndicator('orderDate')}
            </th>
            <th onClick={() => handleSort('customer')} className={styles.sortableHeader}>
              CUSTOMER{getSortIndicator('customer')}
            </th>
            <th className={styles.tableHeader}>ITEMS</th>
            <th onClick={() => handleSort('total')} className={styles.sortableHeader}>
              TOTAL{getSortIndicator('total')}
            </th>
            <th className={styles.tableHeader}>ORDER STATUS</th>
            <th className={styles.tableHeader}>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {currentOrders.map((item) => (
            <tr key={item.orderId}>
              <td>{formatDate(item.orderDate)}</td>
              <td>
                {item.billingInfo ? (
                  <>
                    {item.billingInfo.firstName} {item.billingInfo.lastName}
                    <br />
                    <small>{item.billingInfo.email}</small>
                  </>
                ) : '-'}
              </td>
              <td>
                {item.items?.map((orderItem, index) => (
                  <div key={index}>
                    {orderItem.quantity}x {orderItem.name}
                    {(orderItem.size || orderItem.color) && (
                      ` (${[orderItem.size, orderItem.color].filter(Boolean).join(', ')})`
                    )}
                  </div>
                )) || '-'}
              </td>
              <td>${item.financialInfo?.total?.toFixed(2) || '0.00'}</td>
              <td>
                <select
                  value={item.orderStatus || 1}
                  onChange={e => handleOrderStatusChange(item.orderId, Number(e.target.value))}
                  className={`${styles.orderStatusSelect} ${styles[`orderStatus${item.orderStatus || 1}`]}`}
                  disabled={isSubmitting}
                >
                  {ORDER_STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </td>
              <td>
                <div className={styles.actionButtons}>
                  <button
                    onClick={() => handleViewOrder(item)}
                    className={styles.editButton}
                    disabled={isSubmitting}
                  >
                    View
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filteredAndSortedOrders.length > itemsPerPage && (
        <div className={styles.pagination}>
          {renderPagination()}
        </div>
      )}

      {isModalOpen && selectedOrder && (
        <div className={styles.modalOverlay} onClick={handleModalOverlayClick}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Order Details</h2>
              <button onClick={handleCloseModal} className={styles.closeButton}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.orderDetailsSection}>
                <h3>Order Information</h3>
                <p><strong>Order ID:</strong> {selectedOrder.orderId}</p>
                <p><strong>Order Date:</strong> {formatDate(selectedOrder.orderDate)}</p>
                <p><strong>Status:</strong> {ORDER_STATUS_OPTIONS.find(opt => opt.value === selectedOrder.orderStatus)?.label || 'Pending'}</p>
              </div>

              <div className={styles.orderDetailsSection}>
                <h3>Customer Information</h3>
                {selectedOrder.billingInfo ? (
                  <>
                    <p><strong>Name:</strong> {selectedOrder.billingInfo.firstName} {selectedOrder.billingInfo.lastName}</p>
                    <p><strong>Email:</strong> {selectedOrder.billingInfo.email}</p>
                    <p><strong>Phone:</strong> {selectedOrder.billingInfo.phoneNumber || '-'}</p>
                    <p><strong>Address:</strong> {selectedOrder.billingInfo.address || '-'}</p>
                    <p><strong>City:</strong> {selectedOrder.billingInfo.city || '-'}</p>
                    <p><strong>State:</strong> {selectedOrder.billingInfo.state || '-'}</p>
                    <p><strong>ZIP:</strong> {selectedOrder.billingInfo.zipCode || '-'}</p>
                    <p><strong>Country:</strong> {selectedOrder.billingInfo.country || '-'}</p>
                  </>
                ) : (
                  <p>No billing information available</p>
                )}
              </div>

              <div className={styles.orderDetailsSection}>
                <h3>Order Items</h3>
                <table className={styles.orderItemsTable}>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          {item.name}
                          {(item.size || item.color) && (
                            <small> ({[item.size, item.color].filter(Boolean).join(', ')})</small>
                          )}
                        </td>
                        <td>{item.quantity}</td>
                        <td>${item.priceEach?.toFixed(2) || '0.00'}</td>
                        <td>${((item.priceEach || 0) * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}><strong>Total</strong></td>
                      <td><strong>${selectedOrder.financialInfo?.total?.toFixed(2) || '0.00'}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders; 